"use client";
import { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { serverTimestamp, Timestamp } from "firebase/firestore";
import { useRouter } from "next/navigation";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useOutlet } from "@/context/OutletContext";
import { placeOrder, getProductsByIds } from "@/firebase/firestoreServices";
import { placeOrderWeb, updateOrderPayment } from "@/lib/cashfreeService";
import { ChevronLeft, ChevronRight, Minus, Plus, Trash2, ShoppingCart, MapPinned, ShoppingBag, ArrowRight, Wallet } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import type { Order } from "@/models/order";
import LazyImage from "@/components/LazyImage";

const CartContent = () => {
    const { items, totalItems, totalPrice, updateQuantity, removeItem, clearCart } = useCart();
    const { user, profile } = useAuth();
    const { selectedOutlet, isOutOfRange, setSelectedAddressById, selectedAddressId, outletCity } = useOutlet();
    const router = useRouter();

    const [placing, setPlacing] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<"cod" | "online">("online");
    const [orderNote, setOrderNote] = useState("");
    const [showBillDetails, setShowBillDetails] = useState(false);
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [noteDraft, setNoteDraft] = useState("");
    const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);
    const hasOutOfStockItems = useMemo(() => {
        return items.some(item => item.outOfStock);
    }, [items]);


    // Minimum order — read from outlet, fallback to ₹250
    const MIN_ORDER = selectedOutlet?.minOrder ?? 250;
    const isBelowMinOrder = totalPrice < MIN_ORDER;
    const amountNeeded = Math.ceil(MIN_ORDER - totalPrice);

    // Ref for the min-order fill bar — width set imperatively to avoid inline style lint warning
    const minOrderFillRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (minOrderFillRef.current) {
            minOrderFillRef.current.style.width = `${Math.min((totalPrice / MIN_ORDER) * 100, 100)}%`;
        }
    }, [totalPrice, MIN_ORDER]);

    const addresses = useMemo(() =>
        profile?.addresses ? Object.values(profile.addresses) : [],
        [profile?.addresses]
    );

    const selectedAddress = useMemo(() =>
        profile?.addresses && selectedAddressId ? profile.addresses[selectedAddressId] : null,
        [profile?.addresses, selectedAddressId]
    );

    useEffect(() => {
        const addressIds = addresses.map(a => a.id);
        // Auto-select: if nothing selected, or the selected one doesn't exist anymore
        if (!selectedAddressId || !addressIds.includes(selectedAddressId)) {
            if (profile?.defaultAddressId && addressIds.includes(profile.defaultAddressId)) {
                setSelectedAddressById(profile.defaultAddressId);
            } else if (addresses.length > 0) {
                setSelectedAddressById(addresses[0].id);
            }
        }
    }, [profile?.defaultAddressId, addresses, setSelectedAddressById, selectedAddressId]);

    // Bill calculations
    const gstRate = 0.05;
    const gstAmount = parseFloat((totalPrice * gstRate).toFixed(2));
    const grandTotal = parseFloat((totalPrice + gstAmount).toFixed(2));

    const handlePlaceOrder = async () => {
        if (!user) { toast.error("Please login to place order"); return; }
        if (!selectedAddress) { toast.error("Please add a delivery address"); return; }
        // Only enforce range check if address has actual coordinates
        if (isOutOfRange && selectedAddress?.lat && selectedAddress?.long) {
            const cityMsg = outletCity ? ` Nearest outlet is in ${outletCity}.` : "";
            toast.error(`We don't serve at your location.${cityMsg}`);
            return;
        }
        if (!selectedOutlet) { toast.error("No outlet available. Please try again."); return; }
        if (items.length === 0) return;
        if (hasOutOfStockItems) { toast.error("Some items in your cart are out of stock"); return; }
        if (isBelowMinOrder) {
            toast.error(`Minimum order is ₹${MIN_ORDER}. Add ₹${amountNeeded} more to place your order.`);
            return;
        }

        setPlacing(true);
        try {
            const foodItems: Order["foodItems"] = {};
            const generateId = () => Math.random().toString(36).substring(2, 8);
            items.forEach((item) => {
                const itemId = generateId(); // 6-character random ID like "Ok1naq"
                foodItems![itemId] = {
                    name: item.productName,
                    price: item.price,
                    qty: item.quantity,
                    tax: 5,
                    foodDocId: item.productId,
                    status: true,
                    addons: {},
                };
            });

            const isOnline = paymentMethod === "online";

            // ── Step 1: Save order to Firestore (all Order model fields) ─────────────
            // orderId/transId are NOT set yet — Firestore gives us the doc ID via addDoc
            const firestoreOrderId = await placeOrder({
                uId: user.uid,
                foodItems,
                totalAmountPaid: grandTotal,
                totalTax: gstAmount,
                discountAmount: 0,
                cashOnDelivery: !isOnline,
                isPaid: false,
                isCompleted: false,
                status: isOnline ? "pending_payment" : "Pending",
                type: "Delivery",
                outletDocId: selectedOutlet?.id || "",
                orderNote: orderNote.trim(),
                rewardPointsEarned: 0,
                rewardPointsUsed: 0,
                otp: Math.floor(1000 + Math.random() * 9000), // Random 4 digit OTP
                deliveredOn: null,
                disCoupon: null,
                platform: "web",
                rider: null,
                takeAwayOn: null,
                userAddress: {
                    id: selectedAddressId || "",
                    name: selectedAddress.name || selectedAddress.addressType,
                    location: selectedAddress.location ?? "",
                    city: selectedAddress.city ?? "",
                    area: selectedAddress.area ?? "",
                    flat: selectedAddress.flat ?? "",
                    landmark: selectedAddress.landmark ?? "",
                    addressType: selectedAddress.addressType ?? "",
                    contact: selectedAddress.contact ?? "",
                    lat: selectedAddress.lat ?? 0,
                    long: selectedAddress.long ?? 0,
                    state: selectedAddress.state ?? "Gujarat",
                },
                userData: {
                    docId: user.uid,
                    name: profile?.name ?? "User",
                    number: profile?.phone ?? user.phoneNumber ?? "",
                },
                paymentMethod: {
                    upi: {
                        upi_id: isOnline ? "" : "",  // Firestore rejects undefined — always use empty string
                        channel: null
                    }
                }
            });

            // ── Step 2: Firestore doc ID becomes the receipt / order number ───────────
            // We stamp it back onto the doc so orderId == its own Firestore document ID
            // This same value is passed to the Cloud Function as receiptNo
            const receiptNo = firestoreOrderId;
            await updateOrderPayment(firestoreOrderId, {
                isPaid: false,
                orderId: receiptNo,       // Firestore doc self-stamps its own ID as orderId
                transId: receiptNo,
                status: isOnline ? "pending_payment" : "Pending",
                time: serverTimestamp(),  // Ensure time is set explicitly if not already
            });

            if (!isOnline) {
                // COD — clear cart and go to active orders
                await clearCart();
                toast.success("Order placed successfully! 🎉");
                router.push("/active-orders");
                return;
            }

            // ── Online Payment Flow ──────────────────────────────────────────────
            // Set a session flag so PaymentVerifying knows a real payment is in progress
            sessionStorage.setItem("payment_in_progress", "true");
            // Note: We no longer navigate early. We wait for the modal to close.

            const returnUrl = "https://freshomeat.in";

            const result = await placeOrderWeb({
                amount: grandTotal,
                receiptNo,
                customer_id: user.uid,
                customer_phone: profile?.phone ?? user.phoneNumber ?? "9999999999",
                customer_name: profile?.name ?? "Customer",
                returnUrl,
            });

            // Note: In Next.js we use state patterns or query params. 
            // For now, let's use sessionStorage as a temporary bridge for the complex state if needed,
            // but ideally we refactor the data flow.
            sessionStorage.setItem("payment_data", JSON.stringify({
                result,
                firestoreOrderId,
                receiptNo,
                grandTotal
            }));

            // Navigate to /payment-verifying and pass the results/ids in state
            // The Verifying page will handle updateOrderPayment and clearCart
            router.push("/payment-verifying");
        } catch (e) {
            console.error("Order error:", e instanceof Error ? e.message : e);
            toast.error("Failed to place order. Please try again.");
            router.push("/cart");
        } finally {
            setPlacing(false);
        }
    };

    // ─── Empty / Unauthenticated states ─────────────────────────────────────
    if (!user) {
        return (
            <div className="flex flex-col items-center justify-center py-24 gap-4 px-6 min-h-[50vh]">
                <ShoppingCart className="w-16 h-16 text-muted-foreground/40" />
                <p className="text-lg font-semibold text-foreground">Please login to view your cart</p>
                <button onClick={() => router.push("/account")} className="px-8 py-2.5 bg-primary text-white rounded-full font-semibold text-sm">Login</button>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24 gap-4 px-6 min-h-[50vh]">
                <ShoppingCart className="w-16 h-16 text-muted-foreground/40" />
                <p className="text-lg font-semibold text-foreground">Your cart is empty</p>
                <p className="text-sm text-muted-foreground">Add some delicious products!</p>
                <button onClick={() => router.push("/")} className="px-8 py-2.5 bg-primary text-white rounded-full font-semibold text-sm">Browse Products</button>
            </div>
        );
    }

    // ─── Main Cart ───────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-[#F9F7F7] pb-32">
            {/* Header */}
            <div className="sticky top-0 z-30 bg-[#F9F7F7]/80 backdrop-blur-md">
                <div className="container max-w-2xl mx-auto flex items-center gap-4 px-4 py-6">
                    <button onClick={() => router.back()} className="p-2 -ml-2 rounded-full hover:bg-black/5 transition-colors" aria-label="Back" title="Back">
                        <ChevronLeft className="w-6 h-6 text-foreground" />
                    </button>
                    <h1 className="text-xl font-bold text-[#1A1A1A]">My Cart</h1>
                </div>
            </div>

            <div className="container max-w-2xl mx-auto px-4 space-y-4">
                {/* ── Top Card: Items & Delivery ── */}
                <div className="bg-card rounded-[32px] shadow-sm border border-black/5 overflow-hidden">
                    {/* Cart Items */}
                    <div className="p-1">
                        <AnimatePresence>
                            {items.map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    layout
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className={`flex items-start gap-4 p-4 ${index < items.length - 1 ? "border-b border-black/5" : ""}`}
                                >
                                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-muted flex-shrink-0">
                                        {item.productImage
                                            ? <LazyImage
                                                src={item.productImage}
                                                alt={item.productName}
                                                wrapperClassName="w-full h-full"
                                                className="w-full h-full object-cover"
                                            />
                                            : <div className="w-full h-full flex items-center justify-center text-3xl">🥩</div>
                                        }
                                    </div>
                                    <div className="flex-1 min-w-0 pt-1">
                                        <p className="font-bold text-[15px] text-[#1A1A1A] leading-tight truncate">{item.productName}</p>
                                        <p className="text-xs text-muted-foreground mt-1 font-medium">
                                            {item.variantName || "500g | Curry Cut"}
                                        </p>
                                        <p className="text-base font-bold text-[#1A1A1A] mt-2">₹{item.price}</p>
                                        {item.outOfStock && (
                                            <p className="text-[11px] font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded-md mt-2 w-fit uppercase tracking-wider">
                                                Out of Stock
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center bg-[#F3F3F3] rounded-full p-1 border border-black/5 self-start mt-1">
                                        <button
                                            onClick={() => item.quantity === 1 ? removeItem(item.id) : updateQuantity(item.id, item.quantity - 1)}
                                            className="w-8 h-8 flex items-center justify-center text-black/60 hover:text-black transition-colors"
                                            aria-label={item.quantity === 1 ? "Remove" : "Decrease"}
                                        >
                                            {item.quantity === 1 ? <Trash2 className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                                        </button>
                                        <span className="w-8 text-center text-sm font-bold text-[#1A1A1A]">{item.quantity}</span>
                                        <button
                                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                            className="w-8 h-8 flex items-center justify-center text-black/60 hover:text-black transition-colors"
                                            aria-label="Increase"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    {/* Delivery Address */}
                    <div className="bg-[#F8F2F2]/50 border-t border-black/5 p-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-[#F8F2F2] flex items-center justify-center flex-shrink-0 border border-black/5">
                                <div className="relative">
                                    <div className="absolute inset-0 rounded-full bg-[#a71010] animate-ping opacity-20" />
                                </div>
                                <MapPinned className="w-8 h-8 text-[#8B2323] absolute" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-muted-foreground">Delivering to</p>
                                <p className="font-bold text-[#1A1A1A] text-[15px] truncate mt-0.5">
                                    {selectedAddress ? (selectedAddress.name || selectedAddress.addressType || "Home") : "Set Address"}
                                </p>
                                <p className="text-xs text-muted-foreground truncate font-medium">
                                    {selectedAddress ? [selectedAddress.area, selectedAddress.city].filter(Boolean).join(", ") : "Add your delivery address"}
                                </p>
                                {selectedAddress && isOutOfRange && selectedAddress?.lat && selectedAddress?.long && (
                                    <p className="text-[11px] font-bold text-destructive bg-destructive/10 px-2 py-1 rounded-md mt-1 w-fit uppercase tracking-wider leading-tight">
                                        Outside Delivery Area
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setIsAddressDropdownOpen(!isAddressDropdownOpen)}
                                className="text-sm font-bold text-[#1A1A1A] hover:opacity-70 transition-opacity px-2 py-1"
                            >
                                {isAddressDropdownOpen ? "Close" : "Edit"}
                            </button>
                        </div>

                        {/* Address Selection Dropdown */}
                        <AnimatePresence>
                            {isAddressDropdownOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-hidden border-t border-black/5 mt-4"
                                >
                                    <div className="pt-4 space-y-3">
                                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">Select Delivery Address</p>
                                        <div className="space-y-2">
                                            {addresses.map((addr) => (
                                                <button
                                                    key={addr.id}
                                                    onClick={() => {
                                                        setSelectedAddressById(addr.id);
                                                        setIsAddressDropdownOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${selectedAddressId === addr.id ? "border-[#a71010] bg-[#F8F2F2]" : "border-black/5 bg-transparent hover:border-black/10"
                                                        }`}
                                                >
                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAddressId === addr.id ? "border-[#a71010]" : "border-muted-foreground"}`}>
                                                        {selectedAddressId === addr.id && <div className="w-2 h-2 rounded-full bg-[#a71010]" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-bold text-[#1A1A1A]">{addr.name || addr.addressType || "Address"}</p>
                                                        <p className="text-xs text-muted-foreground truncate">{[addr.area, addr.city].filter(Boolean).join(", ")}</p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => router.push("/address-book")}
                                            className="w-full py-3 rounded-2xl border border-dashed border-black/20 text-xs font-bold text-muted-foreground hover:bg-black/5 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                            Add New Address
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* ── Minimum order banner ── */}
                    {isBelowMinOrder && (
                        <div className="mx-4 mb-4 rounded-2xl bg-orange-50 border border-orange-200 p-4 flex gap-3 items-start">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-base">
                                🛒
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-orange-700 text-sm">Minimum order ₹{MIN_ORDER}</p>
                                <p className="text-xs text-orange-600 mt-0.5 font-medium">
                                    Add items worth <span className="font-bold">₹{amountNeeded}</span> more to place your order.
                                </p>
                                <div className="mt-2 h-1.5 rounded-full bg-orange-200 overflow-hidden">
                                    <div
                                        ref={minOrderFillRef}
                                        className="h-full rounded-full bg-orange-500 transition-all duration-500 w-0"
                                    />
                                </div>
                                <p className="text-[10px] text-orange-500 mt-1 font-medium">₹{totalPrice.toFixed(0)} / ₹{MIN_ORDER}</p>
                            </div>
                        </div>
                    )}

                    {/* Out-of-Range Banner — shown whenever isOutOfRange is true (GPS or address coords) */}
                    {selectedAddress && isOutOfRange && (
                        <div className="mx-4 mb-4 rounded-2xl bg-destructive/10 border border-destructive/20 p-4 flex gap-3 items-start">
                            <div className="w-8 h-8 bg-destructive/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <MapPinned className="w-4 h-4 text-destructive" />
                            </div>
                            <div>
                                <p className="font-bold text-destructive text-sm">Sorry, we don't serve this area</p>
                                <p className="text-xs text-destructive/70 mt-0.5 font-medium">
                                    {selectedAddress.area || selectedAddress.city} is outside our delivery radius.
                                    {outletCity && ` Nearest outlet serves ${outletCity}.`}
                                </p>
                                <button
                                    onClick={() => setIsAddressDropdownOpen(true)}
                                    className="mt-2 text-xs font-bold text-destructive underline underline-offset-2 hover:opacity-80"
                                >
                                    Use a different address
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Mid Card: Payment & Note ── */}
                <div className="bg-card rounded-[32px] shadow-sm border border-black/5 overflow-hidden p-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-[17px] font-bold text-[#1A1A1A]">Payment Method</h2>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => setPaymentMethod("cod")}
                            className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-2xl border-2 transition-all ${paymentMethod === "cod" ? "border-[#8B2323] bg-[#F8F2F2]" : "border-black/5 bg-transparent"
                                }`}
                        >
                            <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === "cod" ? "border-[#8B2323]" : "border-muted-foreground"}`}>
                                {paymentMethod === "cod" && <div className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-[#a71010]" />}
                            </div>
                            <span className="text-[13px] md:text-sm font-bold text-[#1A1A1A]">Cash on Delivery</span>
                            <div className="ml-auto opacity-40">
                                <Wallet className="w-4 h-4 md:w-5 md:h-5" />
                            </div>
                        </button>
                        <button
                            onClick={() => setPaymentMethod("online")}
                            className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-2xl border-2 transition-all relative ${paymentMethod === "online" ? "border-[#8B2323] bg-[#F8F2F2]" : "border-black/5 bg-transparent"
                                }`}
                        >
                            <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === "online" ? "border-[#8B2323]" : "border-muted-foreground"}`}>
                                {paymentMethod === "online" && <div className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-[#a71010]" />}
                            </div>
                            <span className="text-[13px] md:text-sm font-bold text-[#1A1A1A]">Pay Online</span>
                            <div className="ml-auto opacity-40">
                                <svg width="20" height="20" className="md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>
                            </div>
                        </button>
                    </div>

                    <button
                        onClick={() => { setNoteDraft(orderNote); setNoteModalOpen(true); }}
                        className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#F3F3F3] border border-black/5 text-left group hover:bg-black/5 transition-colors"
                    >
                        <svg className="w-5 h-5 text-black/40 group-hover:text-black/60 transition-colors" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" /></svg>
                        <span className="text-sm font-medium text-black/50 truncate flex-1">
                            {orderNote || "Add instructions for delivery (optional)"}
                        </span>
                    </button>
                </div>

                {/* ── Bottom Card: Order Summary ── */}
                <div className="bg-card rounded-[32px] shadow-sm border border-black/5 overflow-hidden p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-[17px] font-bold text-[#1A1A1A]">Order Summary</h2>
                        <span className="text-sm font-bold text-[#1A1A1A]">₹{grandTotal.toFixed(0)}</span>
                    </div>

                    <div className="space-y-4 pt-1">
                        <div className="flex justify-between text-[15px]">
                            <span className="text-black/50 font-medium">Subtotal</span>
                            <span className="font-bold text-[#1A1A1A]">₹{totalPrice.toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between text-[15px]">
                            <span className="text-black/50 font-medium">Tax</span>
                            <span className="font-bold text-[#1A1A1A]">₹{gstAmount.toFixed(0)}</span>
                        </div>
                        <div className="border-t border-black/5 pt-4 flex justify-between">
                            <span className="text-[17px] font-bold text-[#1A1A1A]">Total</span>
                            <span className="text-[17px] font-bold text-[#1A1A1A]">₹{grandTotal.toFixed(0)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Bottom Bar */}
            <div className="fixed bottom-4 md:bottom-6 left-0 right-0 z-50 px-4 pointer-events-none">
                <div className="max-w-3xl mx-auto pointer-events-auto">
                    <div className="bg-white/80 backdrop-blur-xl border border-white/40 rounded-[28px] p-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.1)] flex items-center justify-between">
                        <div className="pl-6 pt-1">
                            <p className="text-xl font-bold text-[#1A1A1A]">₹{grandTotal.toFixed(0)}</p>
                            <p className="text-[11px] text-green-600 font-bold flex items-center gap-1 mt-0.5">
                                <span className="w-3.5 h-3.5 bg-green-500 rounded flex items-center justify-center text-[8px] text-white">✓</span>
                                Incl. all taxes
                            </p>
                        </div>
                        <button
                            onClick={handlePlaceOrder}
                            disabled={placing || !selectedAddress || hasOutOfStockItems || isBelowMinOrder || (isOutOfRange && !!(selectedAddress?.lat && selectedAddress?.long))}
                            className="flex items-center gap-3 bg-[#a71010] hover:bg-[#810606] text-white pl-8 pr-6 py-4 rounded-full font-bold text-[15px] transition-all duration-300 shadow-md transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {placing ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    <ShoppingCart className="w-5 h-5" />
                                    Place Order
                                    <ArrowRight className="w-5 h-5 ml-1" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Add Order Note Modal ── */}
            <AnimatePresence>
                {noteModalOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setNoteModalOpen(false)}
                            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.93, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.93, y: 20 }}
                            transition={{ type: "spring", stiffness: 320, damping: 26 }}
                            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 bg-card rounded-[32px] shadow-2xl p-8 md:max-w-md md:mx-auto"
                        >
                            <h2 className="text-xl font-bold text-[#1A1A1A] mb-6">Order Instructions</h2>
                            <textarea
                                autoFocus
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="E.g. Ring the bell twice..."
                                rows={5}
                                className="w-full rounded-2xl bg-[#F3F3F3] border border-black/5 px-4 py-4 text-sm text-[#1A1A1A] placeholder:text-black/30 resize-none outline-none focus:ring-2 focus:ring-[#8B2323]/20 transition"
                            />
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setNoteModalOpen(false)}
                                    className="flex-1 py-4 rounded-2xl border border-black/5 text-sm font-bold text-[#1A1A1A] hover:bg-black/5 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { setOrderNote(noteDraft.trim()); setNoteModalOpen(false); }}
                                    className="flex-1 py-4 rounded-2xl bg-[#701E1E] text-white text-sm font-bold hover:bg-[#5A1818] transition-colors"
                                >
                                    Save
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function CartPage() {
    return (
        <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>}>
            <CartContent />
        </Suspense>
    );
}
